import React from "react";
import { Cookie, Settings, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ensureAdsenseLoaded } from "@/lib/adsenseLoader";

const CONSENT_KEY = "marstick_cookie_consent_v2";
const SEEN_KEY = "marstick_cookie_preferences_seen_v1";
const DEVICE_SEEN_KEY = "marstick_cookie_preferences_device_seen_v1";

const cookieCopy = {
  en: {
    title: "Cookie preferences",
    intro: "Choose optional analytics and advertising cookies.",
    essential: "Essential",
    essentialText: "Required for security and core app functions.",
    alwaysActive: "Always active",
    analytics: "Analytics",
    analyticsText: "Helps us understand page visits and improve the service.",
    advertising: "Advertising",
    advertisingText: "Supports personalized ads and helps keep Marstick free.",
    reject: "Reject",
    accept: "Accept",
    save: "Save preferences",
    close: "Close cookie preferences",
    settings: "Cookie settings",
  },
  he: {
    title: "העדפות עוגיות",
    intro: "בחרו עוגיות אופציונליות לניתוח שימוש ולפרסום.",
    essential: "חיוניות",
    essentialText: "נדרשות לאבטחה ולפעילות הבסיסית של האפליקציה.",
    alwaysActive: "פעיל תמיד",
    analytics: "ניתוח שימוש",
    analyticsText: "עוזר לנו להבין ביקורים בדפים ולשפר את השירות.",
    advertising: "פרסום",
    advertisingText: "תומך במודעות מותאמות ועוזר לשמור על Marstick חינמית.",
    reject: "דחה",
    accept: "אשר",
    save: "שמור העדפות",
    close: "סגור העדפות עוגיות",
    settings: "הגדרות עוגיות",
  },
};

function getSavedConsent() {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (!saved) return null;
    if (saved === "accepted") return { analytics: true, advertising: true };
    if (saved === "rejected") return { analytics: false, advertising: false };
    return JSON.parse(saved);
  } catch {
    return null;
  }
}

function hasSeenCookiePreferences() {
  return localStorage.getItem(SEEN_KEY) === "true" || localStorage.getItem(DEVICE_SEEN_KEY) === "true" || document.cookie.includes(`${SEEN_KEY}=true`);
}

function markCookiePreferencesSeen() {
  localStorage.setItem(SEEN_KEY, "true");
  localStorage.setItem(DEVICE_SEEN_KEY, "true");
  document.cookie = `${SEEN_KEY}=true; Max-Age=31536000; Path=/; SameSite=Lax`;
}

function isSeoRenderer() {
  const agent = navigator.userAgent.toLowerCase();
  return /bot|crawl|spider|prerender|lighthouse|pagespeed|headlesschrome/.test(agent);
}

function loadAnalyticsScript() {
  if (document.getElementById("marstick-google-analytics")) return;

  const analyticsScript = document.createElement("script");
  analyticsScript.id = "marstick-google-analytics";
  analyticsScript.async = true;
  analyticsScript.src = "https://www.googletagmanager.com/gtag/js?id=G-ML5WLWTQXC";
  document.head.appendChild(analyticsScript);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", "G-ML5WLWTQXC", { send_page_view: false });
}

function updateConsentMode(consent) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag("consent", "update", {
    ad_storage: consent?.advertising ? "granted" : "denied",
    ad_user_data: consent?.advertising ? "granted" : "denied",
    ad_personalization: consent?.advertising ? "granted" : "denied",
    analytics_storage: consent?.analytics ? "granted" : "denied",
  });
}

function loadAdvertisingScript() {
  ensureAdsenseLoaded();
}

function applyConsent(consent) {
  if (!consent) return;
  updateConsentMode(consent);
  if (consent.analytics) loadAnalyticsScript();
  if (consent.advertising) loadAdvertisingScript();
}

export default function CookieConsentBanner() {
  const [consent, setConsent] = React.useState(() => getSavedConsent());
  const [showPanel, setShowPanel] = React.useState(() => !isSeoRenderer() && !getSavedConsent() && !hasSeenCookiePreferences());
  const [preferences, setPreferences] = React.useState(() => getSavedConsent() || { analytics: true, advertising: true });
  const isHebrew = window.location.pathname.startsWith("/he") || document.documentElement.lang === "he";
  const copy = isHebrew ? cookieCopy.he : cookieCopy.en;

  React.useEffect(() => {
    applyConsent(consent);
  }, [consent]);

  React.useEffect(() => {
    if (showPanel) markCookiePreferencesSeen();
  }, [showPanel]);

  React.useEffect(() => {
    const openSettings = () => setShowPanel(true);
    const syncConsent = (event) => {
      if (!event.detail) return;
      setConsent(event.detail);
      setPreferences(event.detail);
    };
    window.addEventListener("marstick:open-cookie-settings", openSettings);
    window.addEventListener("marstick:consent-updated", syncConsent);
    return () => {
      window.removeEventListener("marstick:open-cookie-settings", openSettings);
      window.removeEventListener("marstick:consent-updated", syncConsent);
    };
  }, []);

  const saveConsent = (nextConsent) => {
    markCookiePreferencesSeen();
    localStorage.setItem(CONSENT_KEY, JSON.stringify(nextConsent));
    window.dispatchEvent(new CustomEvent("marstick:consent-updated", { detail: nextConsent }));
    setConsent(nextConsent);
    setPreferences(nextConsent);
    setShowPanel(false);
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {showPanel && (
        <div dir={isHebrew ? "rtl" : "ltr"} data-nosnippet="true" className="fixed bottom-[calc(8.75rem+env(safe-area-inset-bottom,0px))] left-4 right-4 z-30 mx-auto max-w-md rounded-2xl border border-white/40 bg-card/95 p-3 shadow-xl shadow-ocean/15 backdrop-blur-xl sm:left-auto sm:right-5 sm:bottom-5 sm:z-[80] sm:w-[24rem]">
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Cookie className="h-4 w-4" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-base font-bold text-foreground">{copy.title}</h2>
                    <ShieldCheck className="h-3.5 w-3.5 text-success" />
                  </div>
                  <p className="text-xs leading-5 text-muted-foreground">
                    {copy.intro}
                  </p>
                </div>
              </div>
              {consent && (
                <Button variant="ghost" size="icon" onClick={() => setShowPanel(false)} aria-label={copy.close}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid gap-2">
              <div className="rounded-xl border border-border bg-muted/30 p-3">
                <div className="text-sm font-semibold text-foreground">{copy.essential}</div>
                <p className="mt-0.5 text-xs leading-4 text-muted-foreground">{copy.essentialText}</p>
                <div className="mt-1 text-xs font-semibold text-primary">{copy.alwaysActive}</div>
              </div>

              <button
                type="button"
                onClick={() => togglePreference("analytics")}
                className={`rounded-xl border p-3 text-left transition-all ${preferences.analytics ? "border-primary bg-primary/10" : "border-border bg-muted/30"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-foreground">{copy.analytics}</span>
                  <span className={`h-5 w-9 rounded-full p-0.5 transition-colors ${preferences.analytics ? "bg-primary" : "bg-muted-foreground/30"}`}>
                    <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${preferences.analytics ? "translate-x-4" : "translate-x-0"}`} />
                  </span>
                </div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.analyticsText}</p>
              </button>

              <button
                type="button"
                onClick={() => togglePreference("advertising")}
                className={`rounded-xl border p-3 text-left transition-all ${preferences.advertising ? "border-primary bg-primary/10" : "border-border bg-muted/30"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-foreground">{copy.advertising}</span>
                  <span className={`h-5 w-9 rounded-full p-0.5 transition-colors ${preferences.advertising ? "bg-primary" : "bg-muted-foreground/30"}`}>
                    <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${preferences.advertising ? "translate-x-4" : "translate-x-0"}`} />
                  </span>
                </div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.advertisingText}</p>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={() => saveConsent({ analytics: false, advertising: false })} className="gap-1.5 text-xs">
                <X className="h-3.5 w-3.5" /> {copy.reject}
              </Button>
              <Button size="sm" onClick={() => saveConsent({ analytics: true, advertising: true })} className="gap-1.5 text-xs">
                <ShieldCheck className="h-3.5 w-3.5" /> {copy.accept}
              </Button>
              <Button variant="outline" size="sm" onClick={() => saveConsent(preferences)} className="col-span-2 text-xs">
                {copy.save}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!showPanel && consent && (
        <button
          type="button"
          onClick={() => setShowPanel(true)}
          className="sr-only"
          id="cookie-settings-panel-trigger"
        >
          <Settings className="h-4 w-4" /> {copy.settings}
        </button>
      )}
    </>
  );
}